import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Calendar, AlertTriangle, Lock, CheckCircle2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { shortDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import type {
  RenovationItemDto,
  RenovationRoomDto,
} from '@cc/types';

interface Props {
  projectId: string;
  items: RenovationItemDto[];
  rooms: RenovationRoomDto[];
}

interface Group {
  key: string;
  label: string;
  tone: 'danger' | 'warning' | 'default' | 'muted';
  items: RenovationItemDto[];
}

/**
 * Daily-driver schedule surface. Buckets items into Behind / This week /
 * Next week / In 2-4 weeks / Blocked. Items with no plannedStartDate land
 * in "Undated" so they don't disappear.
 */
export function LookAheadList({ projectId, items, rooms }: Props) {
  const groups = bucketItems(items);
  const roomById = new Map(rooms.map((r) => [r.id, r.name]));

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <Card key={g.key}>
          <CardContent className="space-y-2 p-4">
            <div className="flex items-baseline justify-between">
              <h3 className={cn('text-sm font-medium', toneClass(g.tone))}>{g.label}</h3>
              <span className="text-[11px] text-muted-foreground">{g.items.length}</span>
            </div>
            {g.items.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">nothing here</p>
            ) : (
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <ItemRow
                    key={it.id}
                    projectId={projectId}
                    item={it}
                    roomName={roomById.get(it.roomId) ?? '—'}
                    blockedByLookup={items}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ItemRow({
  projectId,
  item,
  roomName,
  blockedByLookup,
}: {
  projectId: string;
  item: RenovationItemDto;
  roomName: string;
  blockedByLookup: RenovationItemDto[];
}) {
  const queryClient = useQueryClient();
  const todayIso = new Date().toISOString().slice(0, 10);

  const markStarted = useMutation({
    mutationFn: () =>
      api.updateItem(projectId, item.id, { actualStartDate: todayIso }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
      toast.success(`Started: ${item.activity}`);
    },
  });
  const markComplete = useMutation({
    mutationFn: () =>
      api.updateItem(projectId, item.id, { actualEndDate: todayIso }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
      toast.success(`Completed: ${item.activity}`);
    },
  });

  const blockedNames = item.derived.blockedByItemIds
    .map((id) => blockedByLookup.find((x) => x.id === id)?.activity)
    .filter((n): n is string => !!n);

  return (
    <motion.li
      initial={{ opacity: 0, y: 2 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium">{item.activity}</p>
          {item.derived.isBlocked && (
            <Badge variant="muted">
              <Lock className="mr-1 h-3 w-3" /> blocked
            </Badge>
          )}
          {item.derived.isBehind && (
            <Badge variant="danger">
              <AlertTriangle className="mr-1 h-3 w-3" /> behind {item.derived.slippageDays}d
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {roomName} · {item.status.toLowerCase().replace('_', ' ')}
          {item.plannedStartDate && (
            <span className="ml-1.5 inline-flex items-center gap-1">
              <Calendar className="h-2.5 w-2.5" />
              {shortDate(item.plannedStartDate)}
              {item.plannedEndDate && item.plannedEndDate !== item.plannedStartDate
                ? `–${shortDate(item.plannedEndDate)}`
                : ''}
            </span>
          )}
          {blockedNames.length > 0 && (
            <span className="ml-1.5">· blocked by {blockedNames.slice(0, 2).join(', ')}</span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {!item.actualStartDate && item.status !== 'COMPLETE' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => markStarted.mutate()}
            disabled={markStarted.isPending}
            title="Mark started"
          >
            <Play className="h-3.5 w-3.5" />
          </Button>
        )}
        {item.status !== 'COMPLETE' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => markComplete.mutate()}
            disabled={markComplete.isPending}
            title="Mark complete"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </motion.li>
  );
}

function bucketItems(items: RenovationItemDto[]): Group[] {
  const todayIso = new Date().toISOString().slice(0, 10);
  const inDays = (d: number) => addDaysIso(todayIso, d);

  const behind: RenovationItemDto[] = [];
  const blocked: RenovationItemDto[] = [];
  const thisWeek: RenovationItemDto[] = [];
  const nextWeek: RenovationItemDto[] = [];
  const soon: RenovationItemDto[] = [];
  const undated: RenovationItemDto[] = [];

  const inThisWeek = inDays(7);
  const inNextWeek = inDays(14);
  const inFourWeeks = inDays(28);

  for (const it of items) {
    if (it.status === 'COMPLETE') continue;
    if (it.derived.isBehind) {
      behind.push(it);
      continue;
    }
    if (it.derived.isBlocked) {
      blocked.push(it);
      continue;
    }
    if (!it.plannedStartDate) {
      undated.push(it);
      continue;
    }
    if (it.plannedStartDate <= inThisWeek) {
      thisWeek.push(it);
    } else if (it.plannedStartDate <= inNextWeek) {
      nextWeek.push(it);
    } else if (it.plannedStartDate <= inFourWeeks) {
      soon.push(it);
    } else {
      undated.push(it);
    }
  }

  const groups: Group[] = [
    { key: 'behind', label: 'Behind', tone: 'danger', items: behind },
    { key: 'this-week', label: 'This week', tone: 'warning', items: thisWeek },
    { key: 'next-week', label: 'Next week', tone: 'default', items: nextWeek },
    { key: 'soon', label: 'In 2–4 weeks', tone: 'default', items: soon },
    { key: 'blocked', label: 'Blocked', tone: 'muted', items: blocked },
    { key: 'undated', label: 'Undated / later', tone: 'muted', items: undated },
  ];
  return groups.filter((g) => g.items.length > 0);
}

function toneClass(tone: Group['tone']): string {
  switch (tone) {
    case 'danger':
      return 'text-rose-400';
    case 'warning':
      return 'text-amber-400';
    case 'muted':
      return 'text-muted-foreground';
    default:
      return '';
  }
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
